import {
  Card,
  Text,
  Group,
  ActionIcon,
  Menu,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPin,
  IconPinnedOff,
} from '@tabler/icons-react';
import type { JournalEntry } from '@/types/journal';

interface JournalEntryCardProps {
  entry: JournalEntry;
  onEdit: (entry: JournalEntry) => void;
  onTogglePin: (entryId: string) => void;
  onDelete: (entryId: string) => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function JournalEntryCard({
  entry,
  onEdit,
  onTogglePin,
  onDelete,
}: JournalEntryCardProps) {
  return (
    <Card withBorder style={{ cursor: 'pointer' }} onClick={() => onEdit(entry)}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <div style={{ minWidth: 0, flex: 1 }}>
          {entry.title && (
            <Text fw={500} lineClamp={1}>
              {entry.title}
            </Text>
          )}
          <Text size="sm" c={entry.title ? 'dimmed' : undefined} lineClamp={3}>
            {entry.content}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            {formatRelativeTime(entry.created_at)}
            {entry.updated_at !== entry.created_at && ' (edited)'}
          </Text>
        </div>

        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEdit(entry);
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={entry.pinned ? <IconPinnedOff size={14} /> : <IconPin size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onTogglePin(entry.id);
              }}
            >
              {entry.pinned ? 'Unpin' : 'Pin to top'}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDelete(entry.id);
              }}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Card>
  );
}
